import { paths } from '../routes/paths';

/** Canonical role strings; mirror backend utils/permission.Role and rename in lockstep. */
export const Role = {
  ADMIN: 'admin',
  USER: 'user',
  FIRMWARE_PUBLISHER: 'firmware_publisher',
  FIRMWARE_MANAGER: 'firmware_manager',
  CONTENT_PUBLISHER: 'content_publisher',
  CONTENT_MANAGER: 'content_manager',
  RESEARCHER: 'researcher',
  FACTORY_OPERATOR: 'factory_operator',
  SUPPORT_AGENT: 'support_agent',
  SLEEP_COACH: 'sleep_coach',
  DEVELOPER: 'developer',
};

// Entries arrive as plain strings or as objects, depending on the endpoint.
const roleName = (entry) =>
  typeof entry === 'string' ? entry : (entry?.name ?? entry?.code ?? entry?.slug ?? entry?.role);

// 'support-agent' and 'SUPPORT AGENT' both mean support_agent; separators have drifted before.
const canonical = (value) =>
  String(value)
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');

const normalizeRoles = (user) => {
  // Take the first field that actually holds entries - `??` alone stops at an
  // empty `role: []` and never falls through to a populated `roles`.
  const raw = [user?.role, user?.roles].find((v) => (Array.isArray(v) ? v.length : v)) ?? [];
  const list = Array.isArray(raw) ? raw : [raw];
  return list.map(roleName).filter(Boolean).map(canonical);
};

export const userHasRole = (user, ...roles) => {
  const owned = normalizeRoles(user);
  return roles.some((r) => owned.includes(r));
};

export const canManageDevices = (user) => userHasRole(user, Role.ADMIN);

export const canUploadFirmware = (user) => userHasRole(user, Role.ADMIN, Role.FIRMWARE_PUBLISHER);

export const canManageFirmware = (user) => userHasRole(user, Role.ADMIN, Role.FIRMWARE_MANAGER);

export const canReadFirmware = (user) =>
  userHasRole(user, Role.ADMIN, Role.FIRMWARE_PUBLISHER, Role.FIRMWARE_MANAGER);

export const canPublishContent = (user) => userHasRole(user, Role.ADMIN, Role.CONTENT_PUBLISHER);

// Mirrors backend IsContentReader - either content role reads every status.
export const canReadContent = (user) =>
  userHasRole(user, Role.ADMIN, Role.CONTENT_PUBLISHER, Role.CONTENT_MANAGER);

export const canConsentToOAuth = (user) => userHasRole(user, Role.ADMIN, Role.RESEARCHER);

// Mirrors backend IsResearcher - the Customer Panel trust boundary.
export const canViewCustomers = (user) => userHasRole(user, Role.ADMIN, Role.RESEARCHER);

export const canManageNotifications = (user) => userHasRole(user, Role.ADMIN);

export const canManageEntitlements = (user) => userHasRole(user, Role.ADMIN, Role.FACTORY_OPERATOR);

export const canManagePlans = (user) => userHasRole(user, Role.ADMIN);

// Mirrors IsSleepCoach; deleting a group remains admin-only.
export const canManageActivityGroups = (user) => userHasRole(user, Role.ADMIN, Role.SLEEP_COACH);

// Mirrors backend IsAdminUser on /admin/version-policies/.
export const canManageAppVersions = (user) => userHasRole(user, Role.ADMIN);

// Mirrors IsCacheOperator's roles. Its active-roster check is invisible here; the page shows the 403.
export const canManageCache = (user) => userHasRole(user, Role.ADMIN, Role.DEVELOPER);

// Since b69e0ef the two consoles are separate surfaces: a coach gets 403 from
// /admin/chat/conversations/ and reads /conversations/coach/ instead.
export const canWorkSupportChat = (user) => userHasRole(user, Role.ADMIN, Role.SUPPORT_AGENT);

// The coach console. Same membership as canViewCoachInsights today, but a different
// trust boundary - Insights is a dashboard, this is the chat surface.
export const canWorkCoachChat = (user) => userHasRole(user, Role.ADMIN, Role.SLEEP_COACH);

// Coach administration - the reassignment queue and a user's standing coach - is
// admin only. A coach calling those endpoints gets 403.
export const canManageSleepCoaches = (user) => userHasRole(user, Role.ADMIN);

// The Ops panel runs on /admin/support/, which is IsAdminUser. A support agent could
// open the screens and get 403s from every call, so the route matches the endpoint.
export const canRunSupportConsole = (user) => userHasRole(user, Role.ADMIN);

// Mirrors backend IsSleepCoach - the Coach Insights trust boundary.
export const canViewCoachInsights = (user) => userHasRole(user, Role.ADMIN, Role.SLEEP_COACH);

// Mirrors backend IsResearcher - the Biosignal trust boundary.
export const canViewBiosignal = (user) => userHasRole(user, Role.ADMIN, Role.RESEARCHER);

// Mirrors backend IsFirmwareReader on /admin/production/: the firmware team runs the line.
export const canViewProduction = (user) => canReadFirmware(user);

// Admin-shell surfaces in landing priority; the first one a user may open is their home.
const ADMIN_HOME_RULES = [
  [(u) => canReadContent(u), paths.dashboard.session.audio.root],
  [(u) => canWorkSupportChat(u), paths.dashboard.support.console],
  [(u) => canViewCustomers(u), paths.dashboard.customers.root],
  [(u) => canReadFirmware(u), paths.dashboard.firmware.root],
  [(u) => canManageEntitlements(u), paths.dashboard.subscriptions.entitlements],
];

// Null when the user has no admin-shell surface (a coach with no other role).
export const adminHomeFor = (user) => ADMIN_HOME_RULES.find(([allow]) => allow(user))?.[1] ?? null;

// Coach-only users land on Insights; everyone else keeps the admin shell, audio list by default.
export const homeFor = (user) => {
  const adminHome = adminHomeFor(user);
  if (!adminHome && userHasRole(user, Role.SLEEP_COACH)) return paths.dashboard.insights.root;
  return adminHome ?? paths.dashboard.session.audio.root;
};

// A dedicated coach must be redirected before any admin shell or page can mount.
export const canAccessAdminShell = (user) =>
  !userHasRole(user, Role.SLEEP_COACH) || adminHomeFor(user) !== null;

export const canManageAssessments = (user) => userHasRole(user, Role.ADMIN);

// Raw ObjectIds are a debugging aid: only an admin who also holds the developer role sees them.
export const canSeeObjectIds = (user) =>
  userHasRole(user, Role.ADMIN) && userHasRole(user, Role.DEVELOPER);

// Any roster role. `user` alone is a customer token, which the staff endpoints refuse.
export const isStaff = (user) =>
  userHasRole(user, ...Object.values(Role).filter((role) => role !== Role.USER));
